import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Controller, FormProvider } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { Box, ConfirmationModal, IconButton, KeyboardDatePicker, MenuItem, Row, Spinner, SplitButton, TextButton, useOverlayContext } from 'seagull';
import { Trash } from 'seagull/icons';
import { LinkIcon } from 'seagull/icons/essentials';
import { MoneySend } from 'seagull/icons/money';
import { getTestIds } from 'seagull/utils/TestIds';
import { TemplateVariablesProvider } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariablesContext';
import AttachFiles from '~/components/AttachFiles/AttachFiles';
import { Attachments } from '~/components/Attachments/Attachments';
import BillChargeTableItem from '~/components/BillClientModal/BillChargeTableItem/BillChargeTableItem';
import { createEmptyChargeItem } from '~/components/BillClientModal/BillClientItem.utils';
import { BillingType } from '~/components/BillClientModal/BillClientModal.types';
import { routes } from '~/constants';
import { useStore } from '~/contexts';
import { useLegalTerms } from '~/hooks/reactQuery/queries/useLegalTerms';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useAttachFiles } from '~/hooks/useAttachFiles';
import { useLocaleKeys } from '~/i18n';
import { ContactModel, ContactAccountantAccess, ContactEditMode, ContactStatus } from '~/models/ContactModel';
import { selectors } from '~/store/state-selectors';
import { connectControllerToKeyboardDatePicker } from '~/utils/react-hook-form.utils';
import styles from './AdHocBillModal.module.scss';
import { AdHocSendOption } from './AdHocBillModal.types';
import { ScheduleDialog } from './ConfirmationDialogs/ScheduleDialog';
import { SendByEmailDialog } from './ConfirmationDialogs/SendByEmailDialog';
import { ShareByLinkDialog } from './ConfirmationDialogs/ShareByLinkDialog';
import { useAdHocApprovalLink } from './hooks/useAdHocApprovalLink';
import { useAdHocBillForm } from './hooks/useAdHocBillForm';
import { useAdHocSubmit } from './hooks/useAdHocSubmit';
import { EmailContentSection } from './sections/EmailContentSection';
import { PaymentSettingsSection } from './sections/PaymentSettingsSection';
import { ADHOC_TEXT_FIELDS, EMPTY_RICH_TEXT_FIELDS } from './sections/templateVariableFields';
const paymentSettingsTextsPath = 'vendorOnboarding.adHocBillModal.paymentSettings';
const emailContentTextsPath = 'vendorOnboarding.adHocBillModal.emailContent';
const sendButtonTextsPath = 'vendorOnboarding.adHocBillModal.sendButton';
export const GetAdHocBillModalTID = (parentTestId)=>getTestIds(parentTestId);
const SendSplitButtonControl = (param)=>{
    let { testIds, isFutureIssueDate, onSelect, texts } = param;
    return /*#__PURE__*/ _jsxs(SplitButton, {
        label: texts.label(),
        testId: testIds.SendSplitButton(),
        onClick: ()=>onSelect(AdHocSendOption.SendByEmail),
        children: [
            /*#__PURE__*/ _jsx(MenuItem, {
                testId: testIds.SendByEmailOption(),
                onClick: ()=>onSelect(AdHocSendOption.SendByEmail),
                children: /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(MoneySend, {
                            size: 16
                        }),
                        texts.sendByEmail()
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(MenuItem, {
                testId: testIds.ShareByLinkOption(),
                disabled: isFutureIssueDate,
                tooltipProps: isFutureIssueDate ? {
                    title: texts.shareByLinkDisabledTooltip(),
                    placement: 'top'
                } : undefined,
                onClick: ()=>onSelect(AdHocSendOption.ShareByLink),
                children: /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(LinkIcon, {
                            size: 16
                        }),
                        texts.shareByLink()
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(MenuItem, {
                testId: testIds.MarkPaidExternallyOption(),
                disabled: isFutureIssueDate,
                tooltipProps: isFutureIssueDate ? {
                    title: texts.markPaidExternallyDisabledTooltip(),
                    placement: 'top'
                } : undefined,
                onClick: ()=>onSelect(AdHocSendOption.MarkPaidExternally),
                children: /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(MoneySend, {
                            size: 16
                        }),
                        texts.markPaidExternally()
                    ]
                })
            })
        ]
    });
};
const buildClientContact = (relationship)=>{
    var _relationship_clientContactId, _relationship_buyer_firstName, _relationship_buyer_lastName;
    return new ContactModel({
        id: (_relationship_clientContactId = relationship.clientContactId) !== null && _relationship_clientContactId !== void 0 ? _relationship_clientContactId : relationship.buyer.id,
        createdAt: 0,
        userId: null,
        firstName: (_relationship_buyer_firstName = relationship.buyer.firstName) !== null && _relationship_buyer_firstName !== void 0 ? _relationship_buyer_firstName : '',
        lastName: (_relationship_buyer_lastName = relationship.buyer.lastName) !== null && _relationship_buyer_lastName !== void 0 ? _relationship_buyer_lastName : '',
        companyName: relationship.customer.name,
        email: relationship.buyer.email,
        phone: relationship.buyer.phone,
        isSample: false,
        edit: {
            mode: ContactEditMode.Readonly
        },
        vendorId: relationship.vendor.id,
        version: '',
        accountantAccess: ContactAccountantAccess.NonCandidate,
        status: ContactStatus.Active,
        hasSignedAgreements: false,
        hasPendingProposals: false,
        integrations: [],
        archiveEligibility: {
            type: 'eligible'
        },
        deleteEligibility: {
            type: 'eligible'
        },
        hasDrafts: false
    });
};
const AdHocBillModalContent = (props)=>{
    var _useStore;
    const { AdHocBillModalTID } = useMemo(()=>GetAdHocBillModalTID(), []);
    const { relationship } = props;
    const { t: globalBillTexts } = useLocaleKeys('components.globalBillModal');
    const { t: adHocTexts } = useLocaleKeys('vendorOnboarding.adHocBillModal');
    const { t: paymentSettingsTexts } = useLocaleKeys(paymentSettingsTextsPath);
    const { t: emailContentTexts } = useLocaleKeys(emailContentTextsPath);
    const { t: sendButtonTexts } = useLocaleKeys(sendButtonTextsPath);
    const { t: commonTexts } = useLocaleKeys('common');
    const businessName = (_useStore = useStore(selectors.user.businessName)) !== null && _useStore !== void 0 ? _useStore : '';
    const worksAt = useStore(selectors.user.business);
    const { attachments, openFilePicker, isUploading, handleRemove } = useAttachFiles();
    const { legalTerms } = useLegalTerms();
    const { startListening, resolveApprovalLink } = useAdHocApprovalLink();
    const { showSuccessToast, showModal } = useOverlayContext();
    const history = useHistory();
    const contact = useMemo(()=>buildClientContact(relationship), [
        relationship
    ]);
    const defaultEmailSubject = emailContentTexts.defaultSubject({
        businessName
    });
    const defaultEmailContent = emailContentTexts.defaultContent();
    const form = useAdHocBillForm({
        defaultEmailSubject,
        defaultEmailContent
    });
    const { isFutureIssueDate } = form;
    const { submitAdHoc } = useAdHocSubmit({
        form,
        relationshipId: props.relationshipId,
        attachments,
        legalTerms,
        defaultEmailSubject,
        defaultEmailContent,
        dismissModal: props.dismissModal
    });
    const [items, includeTermsOfService, coverCreditCardFees, emailSubject, emailContent] = form.watch([
        'items',
        'includeTermsOfService',
        'coverCreditCardFees',
        ...ADHOC_TEXT_FIELDS
    ]);
    const templateVarTextFields = useMemo(()=>[
            emailSubject,
            emailContent
        ], [
        emailSubject,
        emailContent
    ]);
    const buyerContactName = [
        relationship.buyer.firstName,
        relationship.buyer.lastName
    ].filter((part)=>part != null && part.length > 0).join(' ');
    const onAddOutOfScopeCharge = ()=>{
        form.items.append(createEmptyChargeItem());
    };
    const handleDeleteItem = (itemIndex)=>{
        form.items.remove(itemIndex);
    };
    const openSendDialog = ()=>{
        const DialogComponent = isFutureIssueDate ? ScheduleDialog : SendByEmailDialog;
        showModal({
            biName: 'dialog_component_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal: dismissDialog } = param;
                return /*#__PURE__*/ _jsx(DialogComponent, {
                    companyName: relationship.customer.name,
                    contactName: buyerContactName,
                    email: relationship.buyer.email,
                    coverCreditCardFees: coverCreditCardFees,
                    onBack: dismissDialog,
                    onConfirm: ()=>submitAdHoc(AdHocSendOption.SendByEmail, dismissDialog)
                });
            }
        });
    };
    const openShareByLinkDialog = ()=>{
        showModal({
            biName: 'share_by_link_dialog_modal',
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: (param)=>{
                let { dismissModal: dismissDialog } = param;
                return /*#__PURE__*/ _jsx(ShareByLinkDialog, {
                    companyName: relationship.customer.name,
                    contactName: buyerContactName,
                    email: relationship.buyer.email,
                    onCancel: dismissDialog,
                    onGenerateLink: async ()=>{
                        const invoiceIdPromise = startListening(props.relationshipId);
                        const success = await submitAdHoc(AdHocSendOption.ShareByLink);
                        if (!success) {
                            return {
                                success: false
                            };
                        }
                        try {
                            const linkUrl = await resolveApprovalLink(invoiceIdPromise);
                            return {
                                success: true,
                                linkUrl
                            };
                        } catch (unused) {
                            return {
                                success: true,
                                linkUrl: null
                            };
                        }
                    },
                    onClose: ()=>{
                        dismissDialog();
                        showSuccessToast(commonTexts.toasts.chargesSuccessfullyCreated(), 'ad-hoc-bill-share-link-success-toast');
                        history.push(routes.billingHub(props.relationshipId));
                        props.dismissModal();
                    }
                });
            }
        });
    };
    const handleSendOption = (option)=>{
        if (option === AdHocSendOption.SendByEmail) {
            form.handleSubmit(()=>openSendDialog())();
            return;
        }
        if (option === AdHocSendOption.ShareByLink) {
            form.handleSubmit(()=>openShareByLinkDialog())();
            return;
        }
        form.handleSubmit(()=>submitAdHoc(option))();
    };
    const renderTableItem = (item, itemIndex)=>{
        if (item.type !== BillingType.Charge) return null;
        return /*#__PURE__*/ _jsx(BillChargeTableItem, {
            itemIndex: itemIndex,
            chargeItem: item,
            relationship: relationship,
            hideApprovalCallout: true,
            actionColumn: items.length > 1 ? /*#__PURE__*/ _jsx(IconButton, {
                bi: {
                    name: 'delete_ad_hoc_bill_item'
                },
                testId: AdHocBillModalTID.DeleteItem(itemIndex),
                onClick: ()=>handleDeleteItem(itemIndex),
                children: /*#__PURE__*/ _jsx(Trash, {
                    size: 20
                })
            }) : null
        }, item.itemId);
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(ConfirmationModal, {
            bi: {
                primaryButton: {
                    name: 'ad_hoc_bill_1_confirm'
                },
                secondaryButton: {
                    name: 'ad_hoc_bill_1_cancel'
                }
            },
            title: globalBillTexts.billClientTitle({
                customerName: relationship.customer.name
            }),
            width: "1600px",
            testId: AdHocBillModalTID.Root(),
            textAlignCenter: false,
            onCloseButtonClick: props.dismissModal,
            content: worksAt == null ? /*#__PURE__*/ _jsx(Box, {
                className: styles.loadingWrapper,
                testId: AdHocBillModalTID.LoadingSpinner(),
                children: /*#__PURE__*/ _jsx(Spinner, {
                    size: 48
                })
            }) : /*#__PURE__*/ _jsx(TemplateVariablesProvider, {
                contact: contact,
                worksAt: worksAt,
                agreementName: null,
                effectiveDate: null,
                initialTemplateVariableValues: undefined,
                textFields: templateVarTextFields,
                richTextFields: EMPTY_RICH_TEXT_FIELDS,
                children: /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
                    children: /*#__PURE__*/ _jsxs(Box, {
                        className: styles.modalContentWrapper,
                        children: [
                            /*#__PURE__*/ _jsxs(Box, {
                                className: styles.headerRow,
                                children: [
                                    /*#__PURE__*/ _jsx(Controller, {
                                        control: form.control,
                                        name: "issueDate",
                                        rules: requiredValidation(true),
                                        render: (renderProps)=>/*#__PURE__*/ _jsx(KeyboardDatePicker, _object_spread({
                                                bi: {
                                                    name: 'ad_hoc_issue_date'
                                                },
                                                testId: AdHocBillModalTID.IssueDate(),
                                                label: adHocTexts.issueDateLabel()
                                            }, connectControllerToKeyboardDatePicker(renderProps)))
                                    }),
                                    /*#__PURE__*/ _jsx(AttachFiles, {
                                        attachments: attachments,
                                        openFilePicker: openFilePicker,
                                        isUploading: isUploading
                                    })
                                ]
                            }),
                            items.map(renderTableItem),
                            /*#__PURE__*/ _jsx(TextButton, {
                                bi: {
                                    name: 'add_ad_hoc_charge'
                                },
                                testId: AdHocBillModalTID.AddCharge(),
                                underline: "none",
                                weight: "light",
                                onClick: onAddOutOfScopeCharge,
                                children: adHocTexts.addCharge()
                            }),
                            /*#__PURE__*/ _jsxs(Box, {
                                className: styles.sectionsRow,
                                children: [
                                    /*#__PURE__*/ _jsx(PaymentSettingsSection, {
                                        testIds: AdHocBillModalTID.PaymentSettings,
                                        showTermsOfService: includeTermsOfService,
                                        texts: paymentSettingsTexts
                                    }),
                                    /*#__PURE__*/ _jsx(EmailContentSection, {
                                        testIds: AdHocBillModalTID.EmailContent,
                                        texts: emailContentTexts
                                    })
                                ]
                            }),
                            attachments.length > 0 && /*#__PURE__*/ _jsx(Attachments, {
                                files: attachments,
                                onRemove: handleRemove,
                                disableDownloadLinks: true
                            }),
                            /*#__PURE__*/ _jsx(Box, {
                                className: styles.sendButtonRow,
                                children: /*#__PURE__*/ _jsx(SendSplitButtonControl, {
                                    testIds: AdHocBillModalTID,
                                    isFutureIssueDate: isFutureIssueDate,
                                    onSelect: handleSendOption,
                                    texts: sendButtonTexts
                                })
                            })
                        ]
                    })
                }))
            })
        })
    });
};
export const AdHocBillModal = (props)=>{
    const { AdHocBillModalTID } = useMemo(()=>GetAdHocBillModalTID(), []);
    const { t: adHocTexts } = useLocaleKeys('vendorOnboarding.adHocBillModal');
    const { relationship } = useRelationship(props.relationshipId);
    if (relationship == null) {
        return /*#__PURE__*/ _jsx(ConfirmationModal, {
            bi: {
                primaryButton: {
                    name: 'ad_hoc_bill_2_confirm'
                },
                secondaryButton: {
                    name: 'ad_hoc_bill_2_cancel'
                }
            },
            testId: AdHocBillModalTID.Root(),
            title: adHocTexts.title(),
            width: "1600px",
            textAlignCenter: false,
            onCloseButtonClick: props.dismissModal,
            content: /*#__PURE__*/ _jsx(Box, {
                className: styles.loadingWrapper,
                testId: AdHocBillModalTID.LoadingSpinner(),
                children: /*#__PURE__*/ _jsx(Spinner, {
                    size: 48
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(AdHocBillModalContent, _object_spread_props(_object_spread({}, props), {
        relationship: relationship
    }));
};
